import React, { useRef, useEffect } from 'react'
import { Rect, CapsuleProps, VenueStatusColor } from './types'
import useResizeObserver from '@/utils/useResizeObserver'

function VenueStatusProgressBar({
  progress,
  previousProgress = 0,
  minimum = 0,
  maximum = 100,
  backgroundColor = [VenueStatusColor.background_gray, VenueStatusColor.background_gray_2],
  segmentColor = [VenueStatusColor.background_gray],
  progressBarColor = [VenueStatusColor.electric_purple_1],
  borderColor = VenueStatusColor.border_gray,
  animate = false,
  onAnimationComplete = undefined,
}: {
  progress: number | null | undefined
  previousProgress?: number
  minimum?: number
  maximum?: number
  backgroundColor?: Array<string>
  segmentColor?: Array<string>
  progressBarColor?: Array<string>
  borderColor?: string
  animate?: boolean
  onAnimationComplete?: () => void
}) {
  const aspectRatio = 12 / 346
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const dimensions = useResizeObserver(canvasRef)
  const animationDuration = 0.75

  const targetProgress = useRef(50)
  const currentPercentageProgress = useRef(0)
  const timeAccumulator = useRef<DOMHighResTimeStamp>(0)
  const isAnimating = useRef(false)
  const isDrawn = useRef(false)

  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
  const cssWidth = dimensions.width
  const cssHeight = dimensions.width * aspectRatio

  const createColor = (
    ctx: CanvasRenderingContext2D,
    singleOrGradient: Array<string>,
    height: number
  ): string | CanvasGradient => {
    if (singleOrGradient.length === 0) return 'black'
    else if (singleOrGradient.length === 1) return singleOrGradient[0]
    else if (singleOrGradient.length > 1) {
      let count = 0
      let linearGradient = ctx.createLinearGradient(0, 0, 0, height)
      singleOrGradient.forEach((color) => {
        linearGradient.addColorStop(count / (singleOrGradient.length - 1), color)
        count += 1
      })
      return linearGradient
    }

    return 'black'
  }

  const draw = (ctx: CanvasRenderingContext2D) => {
    const canvasW = ctx.canvas.width
    const canvasH = ctx.canvas.height
    ctx.clearRect(0, 0, canvasW, canvasH)

    const leftMargin = 14.5 * dpr
    const rect = new Rect(leftMargin, 0, canvasW - leftMargin * 2, canvasH)

    ctx.save()
    drawBackgroundCapsule(ctx, rect)
    ctx.restore()
    ctx.save()
    drawForegroundCapsule(ctx, rect)
    ctx.restore()
    ctx.save()
    drawDivisionLines(ctx, rect)
    ctx.restore()
  }

  const drawBackgroundCapsule = (ctx: CanvasRenderingContext2D, rect: Rect) => {
    const props = new CapsuleProps(
      rect,
      getCornerRadius(),
      createColor(ctx, backgroundColor, rect.height),
      borderColor,
      2 * dpr
    )
    drawCapsule(ctx, props)
  }

  const drawDivisionLines = (ctx: CanvasRenderingContext2D, rect: Rect) => {
    ctx.fillStyle = createColor(ctx, segmentColor, rect.height)

    for (let i = 1; i < 10; i++) {
      const x = (rect.width / 10) * i
      ctx.fillRect(x, 0, 2 * dpr, ctx.canvas.height)
    }
  }

  const drawForegroundCapsule = (ctx: CanvasRenderingContext2D, rect: Rect) => {
    if (currentPercentageProgress.current <= 0) return

    let value = currentPercentageProgress.current
    value = value > 1 ? value : value + 1

    const newRect = new Rect(rect.x, rect.y, rect.width * (value / 100), rect.height)

    const props = new CapsuleProps(
      newRect,
      getCornerRadius(),
      createColor(ctx, progressBarColor, rect.height),
      borderColor,
      2 * dpr
    )
    drawCapsule(ctx, props)
  }

  const drawCapsule = (ctx: CanvasRenderingContext2D, props: CapsuleProps) => {
    ctx.fillStyle = props.color
    if (props.borderColor != null) {
      ctx.lineWidth = props.borderWidth
      ctx.strokeStyle = props.borderColor
    }
    ctx.beginPath()
    ctx.roundRect(props.rect.x + props.borderWidth, props.rect.y, props.rect.width - props.borderWidth, props.rect.height, [
      props.radius,
    ])
    ctx.fill()
    ctx.stroke()
  }

  const getCornerRadius = (): number => {
    return Math.min(cssWidth * dpr, cssHeight * dpr) / 2
  }

  useEffect(() => {
    const canvas = canvasRef.current as HTMLCanvasElement | null
    if (!canvas) return
    const context = canvas.getContext('2d')
    if (!context) return

    const inverseLerpAndClampToPercentage = (minimum: number, maximum: number, value: number): number => {
      if (maximum < minimum) return 0

      let clampedValue = Math.min(Math.max(value, minimum), maximum)
      let normalized = (clampedValue - minimum) / (maximum - minimum)
      let percentage = normalized * 100

      return Math.min(Math.max(percentage, 0), 100)
    }

    if (animate) {
      targetProgress.current = progress ?? 0
      currentPercentageProgress.current = 0
      timeAccumulator.current = 0
      isAnimating.current = true

      let frameCount = 0
      let animationFrameId = 0
      let lastTime = 0

      const render = (currentTime: DOMHighResTimeStamp) => {
        frameCount++

        if (lastTime === 0) lastTime = currentTime
        const deltaTime = (currentTime - lastTime) / 1000
        lastTime = currentTime

        if (!isAnimating.current) {
          window.cancelAnimationFrame(animationFrameId)
          if (onAnimationComplete && !isDrawn.current) {
            onAnimationComplete()
          }
          isDrawn.current = true
          return
        }

        // Linear interpolation
        let percentage = timeAccumulator.current / animationDuration
        // Quadratic ease out
        percentage = -percentage * (percentage - 2)
        let currentValue = previousProgress + percentage * (targetProgress.current - previousProgress)
        currentPercentageProgress.current = inverseLerpAndClampToPercentage(minimum, maximum, currentValue)

        draw(context)
        timeAccumulator.current += deltaTime
        animationFrameId = window.requestAnimationFrame(render)
        isAnimating.current = timeAccumulator.current < animationDuration
      }

      render(0)
      return () => {
        window.cancelAnimationFrame(animationFrameId)
      }
    } else {
      currentPercentageProgress.current = inverseLerpAndClampToPercentage(minimum, maximum, progress ?? 0)
      draw(context)
      if (onAnimationComplete && !isDrawn.current) {
        onAnimationComplete()
      }
      isDrawn.current = true
    }
  }, [animate, dimensions, progress, dpr])

  return (
    <canvas
      width={cssWidth * dpr}
      height={cssHeight * dpr}
      style={{ height: `${cssHeight}px` }}
      className="w-full"
      ref={canvasRef}
    />
  )
}

export default VenueStatusProgressBar
